import { useState, useEffect, useCallback } from 'react';
import { FamilyMember, Family, Vehicle, Pet } from '../types';
import { lookupSchoolCalendar, saveSchoolCalendar } from '../services';
import { useFamilyStore } from '../stores';
import { useMessage } from './useMessage';

export interface UseFamilyPageReturn {
  // State
  expandedId: number | null;
  showAddForm: boolean;
  lookingUpSchoolFor: number | null;
  message: ReturnType<typeof useMessage>['message'];

  // Toggles
  toggleExpanded: (id: number) => void;
  setShowAddForm: (show: boolean) => void;

  // Member handlers
  handleAddMember: (name: string, role: string) => Promise<void>;
  handleUpdateMember: (id: number, updates: Partial<FamilyMember>) => Promise<void>;
  handleDeleteMember: (id: number, name: string) => void;

  // Family handlers
  handleFamilyUpdate: (updates: Partial<Family>) => void;

  // Vehicle handlers
  handleAddVehicle: (name: string, seats: number) => Promise<void>;
  handleUpdateVehicle: (id: number, updates: Partial<Vehicle>) => Promise<void>;
  handleDeleteVehicle: (id: number, name: string) => void;

  // Pet handlers
  handleAddPet: (name: string, petType: 'dog' | 'cat' | 'other') => Promise<void>;
  handleUpdatePet: (id: number, updates: Partial<Pet>) => Promise<void>;
  handleDeletePet: (id: number, name: string) => void;

  // Invitation handlers
  handleInvite: (email: string) => Promise<void>;
  handleCancelInvitation: (id: string, email: string) => void;
  handleResendInvitation: (id: string, email: string) => void;

  // School lookup
  handleSchoolLookup: (memberId: number) => void;
  handleSchoolNameSaved: (memberId: number, schoolName: string) => void;
}

export function useFamilyPage(): UseFamilyPageReturn {
  const {
    members,
    fetchAll,
    updateFamilyInfo,
    addMember, updateMember, removeMember,
    addVehicle, updateVehicleInfo, removeVehicle,
    addPet, updatePetInfo, removePet,
    invite, cancelInvite, resendInvite,
    error: storeError,
  } = useFamilyStore();

  const { message, showSuccess, showError } = useMessage();

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [lookingUpSchoolFor, setLookingUpSchoolFor] = useState<number | null>(null);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (storeError) showError(storeError); }, [storeError]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpanded = useCallback((id: number) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  // --- Member handlers ---

  const handleAddMember = useCallback(async (name: string, role: string) => {
    try {
      const member = await addMember({ name, role });
      setShowAddForm(false);
      setExpandedId(member.id);
      showSuccess(`Added ${member.name}. Now tell me more about them.`);
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to add');
      throw err;
    }
  }, [addMember, showSuccess, showError]);

  const handleUpdateMember = useCallback(async (id: number, updates: Partial<FamilyMember>) => {
    try {
      await updateMember(id, updates);
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to update');
    }
  }, [updateMember, showError]);

  const handleDeleteMember = useCallback((id: number, name: string) => {
    if (!window.confirm(`Remove ${name} from your family?`)) return;
    removeMember(id)
      .then(() => { setExpandedId(null); showSuccess(`${name} removed.`); })
      .catch(() => showError('Failed to remove'));
  }, [removeMember, showSuccess, showError]);

  // --- Family handlers ---

  const handleFamilyUpdate = useCallback((updates: Partial<Family>) => {
    updateFamilyInfo(updates).catch((err: unknown) => {
      showError(err instanceof Error ? err.message : 'Failed to update');
    });
  }, [updateFamilyInfo, showError]);

  // --- Vehicle handlers ---

  const handleAddVehicle = useCallback(async (name: string, seats: number) => {
    try {
      const vehicle = await addVehicle({ name, seats });
      showSuccess(`Added ${vehicle.name}.`);
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to add');
      throw err;
    }
  }, [addVehicle, showSuccess, showError]);

  const handleUpdateVehicle = useCallback(async (id: number, updates: Partial<Vehicle>) => {
    try {
      await updateVehicleInfo(id, updates);
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to update');
    }
  }, [updateVehicleInfo, showError]);

  const handleDeleteVehicle = useCallback((id: number, name: string) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    removeVehicle(id)
      .then(() => showSuccess(`${name} removed.`))
      .catch(() => showError('Failed to remove'));
  }, [removeVehicle, showSuccess, showError]);

  // --- Pet handlers ---

  const handleAddPet = useCallback(async (name: string, petType: 'dog' | 'cat' | 'other') => {
    try {
      const pet = await addPet({ name, pet_type: petType });
      showSuccess(`Added ${pet.name}.`);
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to add');
      throw err;
    }
  }, [addPet, showSuccess, showError]);

  const handleUpdatePet = useCallback(async (id: number, updates: Partial<Pet>) => {
    try {
      await updatePetInfo(id, updates);
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to update');
    }
  }, [updatePetInfo, showError]);

  const handleDeletePet = useCallback((id: number, name: string) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    removePet(id)
      .then(() => showSuccess(`${name} removed.`))
      .catch(() => showError('Failed to remove'));
  }, [removePet, showSuccess, showError]);

  // --- Invitation handlers ---

  const handleInvite = useCallback(async (email: string) => {
    try {
      await invite(email);
      showSuccess('Invitation sent.');
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to send');
      throw err;
    }
  }, [invite, showSuccess, showError]);

  const handleCancelInvitation = useCallback((id: string, email: string) => {
    if (!window.confirm(`Cancel invitation to ${email}?`)) return;
    cancelInvite(id)
      .then(() => showSuccess('Invitation cancelled.'))
      .catch((err: unknown) => showError(err instanceof Error ? err.message : 'Failed to cancel'));
  }, [cancelInvite, showSuccess, showError]);

  const handleResendInvitation = useCallback((id: string, email: string) => {
    resendInvite(id)
      .then(() => showSuccess(`Invitation resent to ${email}.`))
      .catch((err: unknown) => showError(err instanceof Error ? err.message : 'Failed to resend'));
  }, [resendInvite, showSuccess, showError]);

  // --- School calendar lookup ---

  const handleSchoolLookup = useCallback((memberId: number) => {
    const member = members.find(m => m.id === memberId);
    if (!member?.school_name || member.school_name.length < 3) return;
    if (member.school_calendar_data_name) return;

    setLookingUpSchoolFor(memberId);

    let city = '';
    let state = '';
    if (member.school_location) {
      const parts = member.school_location.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        city = parts[parts.length - 2];
        state = parts[parts.length - 1].split(' ')[0];
      }
    }

    lookupSchoolCalendar(member.school_name, city, state)
      .then(async (result) => {
        if (result.found && result.confidence > 0.5) {
          await saveSchoolCalendar({
            school_name: result.school_name,
            school_district: result.school_district,
            city: result.city,
            state: result.state,
            school_year: result.school_year,
            first_day: result.first_day || '',
            last_day: result.last_day || '',
            breaks: result.breaks || [],
            confidence: result.confidence,
            is_verified: false,
          });
          await updateMember(memberId, {
            school_calendar_data_name: result.school_name,
            school_calendar_data_year: result.school_year,
          });
          const breakCount = result.breaks?.length || 0;
          showSuccess(`Found ${result.school_name}'s calendar with ${breakCount} breaks. I'll track it.`);
        }
      })
      .catch((err) => {
        console.error('School calendar lookup failed:', err);
      })
      .finally(() => {
        setLookingUpSchoolFor(null);
      });
  }, [members, updateMember, showSuccess]);

  // Auto-trigger school lookup after school name is saved on blur
  // Matches original behavior: lookup runs when name >= 5 chars and no calendar linked
  const handleSchoolNameSaved = useCallback((memberId: number, schoolName: string) => {
    if (!schoolName || schoolName.length < 5) return;
    const member = members.find(m => m.id === memberId);
    if (member?.school_calendar_data_name) return;
    handleSchoolLookup(memberId);
  }, [members, handleSchoolLookup]);

  return {
    expandedId,
    showAddForm,
    lookingUpSchoolFor,
    message,
    toggleExpanded,
    setShowAddForm,
    handleAddMember,
    handleUpdateMember,
    handleDeleteMember,
    handleFamilyUpdate,
    handleAddVehicle,
    handleUpdateVehicle,
    handleDeleteVehicle,
    handleAddPet,
    handleUpdatePet,
    handleDeletePet,
    handleInvite,
    handleCancelInvitation,
    handleResendInvitation,
    handleSchoolLookup,
    handleSchoolNameSaved,
  };
}
